import type { ReimbursementDocumentTrigger } from '../types/reimbursementFormTypes'

/**
 * Legacy `resident_default.document_status` → Reimbursement 3.0 `reimbursement.document_status`.
 * @see backfill migration `document_status` column mapping
 */
const LEGACY_DOCUMENT_STATUS_TO_NEW: Record<string, string> = {
  RECEIVED: 'DOCS_APPROVED',
  UPLOADED: 'UPLOADED',
  MISSING: 'AWAITING',
  REJECTED: 'AWAITING',
}

const DOCUMENT_TRIGGER_LABELS: Record<ReimbursementDocumentTrigger, string> = {
  awaiting: 'Awaiting',
  uploaded: 'Uploaded',
  docs_approved: 'Docs Approved',
}

/** `document_status` query param values — matches `reimbursement.document_status` in the API. */
export const DOCUMENT_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'AWAITING', label: DOCUMENT_TRIGGER_LABELS.awaiting },
  { value: 'UPLOADED', label: DOCUMENT_TRIGGER_LABELS.uploaded },
  { value: 'DOCS_APPROVED', label: DOCUMENT_TRIGGER_LABELS.docs_approved },
]

/** Normalizes stored or legacy filter values before calling the list API. */
export function normalizeDocumentStatusFilter(value: string | null | undefined): string {
  return normalizeReimbursementDocumentStatusCode(value)
}

/** Normalize API or legacy codes to Reimbursement 3.0 document_status codes (uppercase). */
export function normalizeReimbursementDocumentStatusCode(
  raw: string | null | undefined,
): string {
  const trimmed = String(raw ?? '').trim()
  if (!trimmed) return ''
  const upper = trimmed.toUpperCase().replace(/\s+/g, '_')
  return LEGACY_DOCUMENT_STATUS_TO_NEW[upper] ?? upper
}

export function reimbursementDocumentStatusToTrigger(
  raw: string | null | undefined,
): ReimbursementDocumentTrigger | null {
  const code = normalizeReimbursementDocumentStatusCode(raw).toLowerCase()
  if (!code || code === 'awaiting') return 'awaiting'
  if (code === 'docs_approved') return 'docs_approved'
  if (code === 'uploaded') return 'uploaded'

  // Legacy display strings not covered by the migration map
  if (['received', 'approved', 'complete', 'completed'].includes(code)) {
    return 'docs_approved'
  }
  if (['missing', 'rejected'].includes(code)) return 'awaiting'
  if (['pending', 'incomplete'].includes(code)) return 'uploaded'

  return 'uploaded'
}

export function isReimbursementDocumentStatusApproved(
  raw: string | null | undefined,
): boolean {
  return reimbursementDocumentStatusToTrigger(raw) === 'docs_approved'
}

export function displayReimbursementDocumentStatus(raw: string | null | undefined): string {
  const trigger = reimbursementDocumentStatusToTrigger(raw)
  if (!trigger) return ''
  return DOCUMENT_TRIGGER_LABELS[trigger]
}
